import {
    useEffect,
    useState,
} from 'react';

import api from '../../api/api';

import AdminLayout from './Components/AdminLayout';
import BlogEditorModal from './Components/BlogEditorModal';
import {IMAGE_URL} from "../../path";

type Blog = {

    id: number;

    title: string;

    subtitle: string;

    slug: string;

    cover_image: string | null;

    published_at: string | null;

    draft: boolean;

    is_active: boolean;

    display_order: number;

    components_count: number;

    category: {

        id: number;

        name: string;

    } | null;

};

export default function AdminBlogs() {
    const [
        blogs,
        setBlogs,
    ] = useState<Blog[]>([]);

    const [
        selectedBlog,
        setSelectedBlog,
    ] = useState<Blog | null>(
        null
    );

    const [
        modalOpen,
        setModalOpen,
    ] = useState(false);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [coverImage, setCoverImage] =
        useState<File | null>(null);

    const [coverPreview, setCoverPreview] =
        useState('');

    const [headerBody, setHeaderBody] =
        useState('');

    const [saving, setSaving] =
        useState(false);

    const [page, setPage] =
        useState<any>(null);

    useEffect(() => {
        loadPage();
    }, []);

    const getImageUrl = (
        path?: string
    ) => {

        if (!path) {
            return '';
        }

        if (
            path.startsWith('http')
        ) {
            return path;
        }

        return `${IMAGE_URL}${path}`;
    };

    const loadPage = async () => {

        setLoading(true);

        try {

            const pageResponse =
                await api.get(
                    '/page-header/blog'
                );

            const blogsResponse =
                await api.get(
                    '/blogs'
                );

            setBlogs(
                blogsResponse.data
            );

            const data =
                pageResponse.data.page;

            setPage(
                data
            );

            setHeaderBody(
                data?.header_body || ''
            );

            if (
                data?.cover_image
            ) {

                setCoverPreview(
                    getImageUrl(
                        data.cover_image
                    )
                );
            }

        } finally {

            setLoading(false);

        }
    };

    const handleSave = async () => {

        try {

            setSaving(
                true
            );

            if (
                coverImage
            ) {

                const formData =
                    new FormData();

                formData.append(
                    'image',
                    coverImage
                );

                await api.post(
                    '/page-header/blog/cover-image',
                    formData
                );
            }

            await api.put(
                '/page-header/blog/header-body',
                {
                    header_body:
                    headerBody,
                }
            );

            await loadPage();

            setCoverImage(
                null
            );

            alert(
                'Blogs page updated successfully.'
            );

        } catch (
            error
            ) {

            console.error(
                error
            );

            alert(
                'Failed to save changes.'
            );

        } finally {

            setSaving(
                false
            );

        }
    };

    const handleBlogSave = async (
        formData: FormData
    ) => {

        try {

            if (selectedBlog) {


                formData.append("_method", "PUT");

                await api.post(
                    `/blogs/${selectedBlog.id}`,
                    formData
                );

            } else {

                await api.post(
                    "/blogs",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

            }

            await loadPage();

            setModalOpen(false);
            setSelectedBlog(null);

            alert(
                `Blog ${
                    selectedBlog
                        ? "updated"
                        : "created"
                } successfully.`
            );

        } catch (error: any) {

            console.error(error);

            if (error.response) {

                console.log(error.response.data);

            }

            alert(
                "Failed to save blog."
            );

        }

    };

    const handleDelete = async (
        id: number
    ) => {
        if (
            !window.confirm(
                'Delete this blog?'
            )
        ) {
            return;
        }

        await api.delete(
            `/blogs/${id}`
        );

        await loadPage();

    };

    const moveUp = async (
        index: number
    ) => {
        if (
            index === 0
        ) {
            return;
        }

        const updated =
            [...blogs];

        [

            updated[index],

            updated[index - 1],

        ] = [

            updated[index - 1],

            updated[index],

        ];

        await saveOrder(
            updated
        );
    };

    const moveDown = async (
        index: number
    ) => {
        if (
            index ===
            blogs.length - 1
        ) {
            return;
        }

        const updated =
            [...blogs];

        [

            updated[index],

            updated[index + 1],

        ] = [

            updated[index + 1],

            updated[index],

        ];

        await saveOrder(
            updated
        );
    };

    const saveOrder = async (
        ordered: Blog[]
    ) => {
        const payload =
            ordered.map(
                (
                    blog,
                    index
                ) => ({

                    id:
                    blog.id,

                    display_order:
                        index + 1,

                })
            );

        await api.post(
            '/blogs/reorder',

            {

                blogs:
                payload,

            }
        );

        loadPage();
    };

    return (

        <AdminLayout>

            <div className="page-header">

                <h1>
                    Blogs
                </h1>

                <div className="actions">

                    <button
                        className="action-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>

                    <button
                        className="action-btn"
                        onClick={() => {

                            setSelectedBlog(
                                null
                            );

                            setModalOpen(
                                true
                            );

                        }}
                    >
                        Add Blog
                    </button>

                </div>

            </div>

            <div className="admin-card">

                <h2>
                    Cover Image
                </h2>

                {coverPreview && (

                    <img
                        src={coverPreview}
                        alt=""
                        className="image-preview"
                    />

                )}

                <p className="field-hint">
                    Maximum file size: 2MB
                </p>

                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {

                        const file =
                            e.target.files?.[0];

                        if (!file) {
                            return;
                        }

                        if (
                            file.size >
                            2 * 1024 * 1024
                        ) {

                            alert(
                                'Image must be 2MB or smaller.'
                            );

                            return;
                        }

                        setCoverImage(file);

                        setCoverPreview(
                            URL.createObjectURL(file)
                        );

                    }}
                />

            </div>

            <div className="admin-card description">

                <label>
                    Header Body
                </label>

                <textarea
                    value={headerBody}
                    onChange={(e) =>
                        setHeaderBody(
                            e.target.value
                        )
                    }
                    rows={8}
                    className="form-control"
                />

            </div>

            {

                loading

                    ?

                    (

                        <div className="loading">

                            Loading...

                        </div>

                    )

                    :

                    (

                        <table className="users-table">

                            <thead>

                            <tr>

                                <th></th>

                                <th>Title</th>

                                <th>Category</th>

                                <th>Published</th>

                                <th>Draft</th>

                                <th>Active</th>

                                <th>Components</th>

                                <th>Actions</th>

                            </tr>

                            </thead>

                            <tbody>

                            {

                                blogs.map(
                                    (
                                        blog,
                                        index
                                    ) => (

                                        <tr
                                            key={
                                                blog.id
                                            }
                                        >

                                            <td>

                                                {

                                                    blog.cover_image

                                                        ?

                                                        (

                                                            <img

                                                                src={
                                                                    `${IMAGE_URL}${blog.cover_image}`
                                                                }

                                                                alt={
                                                                    blog.title
                                                                }

                                                                className="table-thumbnail"

                                                            />

                                                        )

                                                        :

                                                        '-'

                                                }

                                            </td>

                                            <td>

                                                {blog.title}

                                            </td>

                                            <td>

                                                {

                                                    blog.category?.name ??

                                                    '-'

                                                }

                                            </td>

                                            <td>

                                                {

                                                    blog.published_at

                                                        ?

                                                        new Date(
                                                            blog.published_at
                                                        ).toLocaleDateString()

                                                        :

                                                        '-'

                                                }

                                            </td>

                                            <td>

                                                {

                                                    blog.draft

                                                        ?

                                                        'Yes'

                                                        :

                                                        'No'

                                                }

                                            </td>

                                            <td>

                                                {

                                                    blog.is_active

                                                        ?

                                                        'Yes'

                                                        :

                                                        'No'

                                                }

                                            </td>

                                            <td>

                                                {

                                                    blog.components_count

                                                }

                                            </td>

                                            <td>

                                                <div className="actions">

                                                    <button

                                                        className="move-btn"

                                                        disabled={
                                                            index === 0
                                                        }

                                                        onClick={() =>
                                                            moveUp(
                                                                index
                                                            )
                                                        }

                                                    >

                                                        ↑

                                                    </button>

                                                    <button

                                                        className="move-btn"

                                                        disabled={
                                                            index ===
                                                            blogs.length - 1
                                                        }

                                                        onClick={() =>
                                                            moveDown(
                                                                index
                                                            )
                                                        }

                                                    >

                                                        ↓

                                                    </button>

                                                    <button

                                                        className="edit-btn"

                                                        onClick={async () => {

                                                            const response =
                                                                await api.get(
                                                                    `/blogs/${blog.id}`
                                                                );

                                                            setSelectedBlog(
                                                                {

                                                                    ...response.data.blog,

                                                                    components:
                                                                    response.data.components,

                                                                }
                                                            );

                                                            setModalOpen(
                                                                true
                                                            );

                                                        }}

                                                    >

                                                        Edit

                                                    </button>

                                                    <button

                                                        className="delete-btn"

                                                        onClick={() =>
                                                            handleDelete(
                                                                blog.id
                                                            )
                                                        }

                                                    >

                                                        Delete

                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    )
                                )

                            }

                            </tbody>

                        </table>

                    )

            }

            {

                modalOpen && (

                    <BlogEditorModal

                        blog={
                            selectedBlog
                        }

                        onClose={() => {

                            setModalOpen(
                                false
                            );

                            setSelectedBlog(
                                null
                            );

                        }}

                        onSave={handleBlogSave}

                    />

                )

            }

        </AdminLayout>

    );
}
